<script setup>
import { ref, reactive, nextTick } from "vue";
import { Modal } from "bootstrap";
import { useResidentStore } from "@/stores/resident";
import { useUnitStore } from "@/stores/unit";

const emit = defineEmits(["saved"]);

const residentStore = useResidentStore();
const unitStore = useUnitStore();
const modalEl = ref(null);
const isEditing = ref(false);

// ─── Form & Errors ────────────────────────────────────
const defaultForm = () => ({
  _id: null,
  name: "",
  residentType: "owner",
  unitCode: "",
  houseNo: "",
  building: "",
  zone: "",
  phone: "",
  email: "",
  address: "",
  moveInDate: "",
  occupancyStatus: "active",
  emergencyContact: "",
  emergencyPhone: "",
  note: "",
});

const form = reactive(defaultForm());
const errors = reactive({ name: "", unitCode: "", phone: "" });

// ─── Helpers ──────────────────────────────────────────
const getModal = () => Modal.getOrCreateInstance(modalEl.value);

const resetForm = () => {
  Object.assign(form, defaultForm());
  Object.keys(errors).forEach((k) => (errors[k] = ""));
};

// auto-fill ข้อมูลยูนิตเมื่อเลือก
const onUnitChange = () => {
  const unit = unitStore.units.find((u) => u.unitCode === form.unitCode);
  form.houseNo = unit?.houseNo || "";
  form.building = unit?.building || "";
  form.zone = unit?.zone || "";
  form.address = unit
    ? `${unit.houseNo} ${unit.building} โครงการ Central Fee Residence`
    : "";
};

const validate = () => {
  errors.name = form.name ? "" : "กรุณากรอกชื่อ-นามสกุล";
  errors.unitCode = form.unitCode ? "" : "กรุณาเลือกยูนิต";
  errors.phone = form.phone ? "" : "กรุณากรอกเบอร์โทรศัพท์";
  return !Object.values(errors).some(Boolean);
};

// ─── Public API ───────────────────────────────────────
/**
 * open(resident?)
 * - ไม่ส่ง resident → โหมดเพิ่มใหม่
 * - ส่ง resident   → โหมดแก้ไข
 */
const open = (resident = null) => {
  resetForm();
  if (resident) {
    isEditing.value = true;
    Object.assign(form, {
      _id: resident.id,
      name: resident.name,
      residentType: resident.residentType,
      unitCode: resident.unitCode,
      houseNo: resident.houseNo || "",
      building: resident.building || "",
      zone: resident.zone || "",
      phone: resident.phone || "",
      email: resident.email || "",
      address: resident.address || "",
      moveInDate: resident.moveInDate || "",
      occupancyStatus: resident.occupancyStatus || "active",
      emergencyContact: resident.emergencyContact || "",
      emergencyPhone: resident.emergencyPhone || "",
      note: resident.note || "",
    });
  } else {
    isEditing.value = false;
  }
  nextTick(() => getModal().show());
};

const close = () => getModal().hide();

// ─── Submit ───────────────────────────────────────────
const handleSubmit = () => {
  if (!validate()) return;

  if (isEditing.value) {
    residentStore.updateResident({ ...form, id: form._id });
  } else {
    residentStore.addResident({ ...form });
  }

  close();
  emit("saved", { mode: isEditing.value ? "edit" : "add" });
};

defineExpose({ open, close });
</script>
<template>
  <div class="modal fade" tabindex="-1" ref="modalEl">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content rounded-4 border-0 shadow">
        <div class="modal-header border-0 pb-0 px-4 pt-4">
          <h5 class="modal-title fw-bold">
            <i class="bi bi-person-circle me-1 text-primary"></i
            >{{ isEditing ? "แก้ไขข้อมูลลูกบ้าน" : "เพิ่มลูกบ้านใหม่" }}
          </h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>

        <div class="modal-body px-4 py-3">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label fw-semibold">
                ชื่อ-นามสกุล <span class="text-danger">*</span>
              </label>
              <input
                v-model="form.name"
                type="text"
                class="form-control rounded-3"
                placeholder="เช่น สมชาย ใจดี"
                :class="{ 'is-invalid': errors.name }"
              />
              <div class="invalid-feedback">{{ errors.name }}</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">ประเภท</label>
              <select v-model="form.residentType" class="form-select rounded-3">
                <option value="owner">เจ้าของ</option>
                <option value="tenant">ผู้เช่า</option>
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">
                ยูนิต <span class="text-danger">*</span>
              </label>
              <select
                v-model="form.unitCode"
                class="form-select rounded-3"
                :class="{ 'is-invalid': errors.unitCode }"
                :disabled="isEditing"
                @change="onUnitChange"
              >
                <option value="">-- เลือกยูนิต --</option>
                <option
                  v-for="u in unitStore.units"
                  :key="u.id"
                  :value="u.unitCode"
                >
                  {{ u.unitCode }} ({{ u.building }})
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.unitCode }}</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">บ้านเลขที่</label>
              <input
                :value="form.houseNo"
                type="text"
                class="form-control rounded-3 bg-light"
                readonly
              />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">
                เบอร์โทรศัพท์ <span class="text-danger">*</span>
              </label>
              <input
                v-model="form.phone"
                type="tel"
                class="form-control rounded-3"
                placeholder="เช่น 081-234-5678"
                :class="{ 'is-invalid': errors.phone }"
              />
              <div class="invalid-feedback">{{ errors.phone }}</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">อีเมล</label>
              <input
                v-model="form.email"
                type="email"
                class="form-control rounded-3"
                placeholder="example@email.com"
              />
            </div>

            <div class="col-12">
              <label class="form-label fw-semibold">ที่อยู่</label>
              <input
                v-model="form.address"
                type="text"
                class="form-control rounded-3"
                placeholder="ที่อยู่ปัจจุบัน"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">วันที่เข้าอยู่</label>
              <input
                v-model="form.moveInDate"
                type="date"
                class="form-control rounded-3"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">สถานะ</label>
              <select
                v-model="form.occupancyStatus"
                class="form-select rounded-3"
              >
                <option value="active">กำลังพักอาศัย</option>
                <option value="inactive">ย้ายออก</option>
              </select>
            </div>

            <!-- ผู้ติดต่อฉุกเฉิน -->
            <div class="col-12">
              <div
                class="fw-semibold text-secondary small mb-2 border-top pt-3"
              >
                ผู้ติดต่อฉุกเฉิน
              </div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">ชื่อผู้ติดต่อ</label>
              <input
                v-model="form.emergencyContact"
                type="text"
                class="form-control rounded-3"
                placeholder="ชื่อ-นามสกุล"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">เบอร์โทร</label>
              <input
                v-model="form.emergencyPhone"
                type="tel"
                class="form-control rounded-3"
                placeholder="เช่น 089-000-1111"
              />
            </div>

            <div class="col-12">
              <label class="form-label fw-semibold">หมายเหตุ</label>
              <textarea
                v-model="form.note"
                class="form-control rounded-3"
                rows="2"
                placeholder="บันทึกเพิ่มเติม"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="modal-footer border-0 px-4 pb-4 pt-2 gap-2">
          <button
            type="button"
            class="btn btn-danger border rounded-3 px-4"
            @click="close"
          >
            <i class="bi bi-x-lg me-1"></i>ยกเลิก
          </button>
          <button
            type="button"
            class="btn rounded-3 px-4"
            :class="isEditing ? 'btn-warning' : 'btn-success'"
            @click="handleSubmit"
          >
            <i class="bi bi-floppy2 me-1"></i
            >{{ isEditing ? "บันทึกการแก้ไข" : "เพิ่มลูกบ้าน" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
